import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Input, InputNumber, Switch } from 'antd';
import React from 'react';
import { LabeledField } from '../../../components/form-fields/labeled-field';
import { Section } from '../../../components/form-fields/section';
import { ImageInput } from '../../../components/image-input';
import { Doc31Badge, Doc31Bonus } from '../../../types/doc31';
import { doc31FieldId } from '../../../utils/doc31';
import { LANGUAGE_NAMES, LANGUAGES, SetupField, SetupFormApi } from './fields';
import style from './style.module.scss';

export type CatalogKind = 'bonuses' | 'badges';

/** Counting badge rules: the only ones with a goal to reach. */
const THRESHOLD_LABELS: Record<string, string> = {
    distinct_play_days: 'Days to play',
    successful_referrals: 'Referrals',
};

interface CatalogProps {
    form: SetupFormApi;
    /** Keys of the open cards. */
    expandedKeys: string[];
    onToggleExpanded: (kind: CatalogKind, key: string) => void;
    /** The section's own save, on its title row. */
    saveAction: React.ReactNode;
}

interface ItemFieldProps<T extends Doc31Bonus | Doc31Badge> {
    form: SetupFormApi;
    kind: CatalogKind;
    item: T;
    /** Position in the form's list: formik paths use it, API paths the key. */
    index: number;
}

const NumberField = <T extends Doc31Bonus | Doc31Badge>({
    form,
    kind,
    item,
    index,
    field,
    value,
    nullable = false,
    label,
    min,
}: ItemFieldProps<T> & {
    field: 'entries' | 'sortOrder' | 'maxPerUser' | 'maxPerDay' | 'threshold';
    value: number | null;
    /** Empty means no limit; otherwise clearing keeps the last number. */
    nullable?: boolean;
    label: string;
    min: number;
}) => (
    <SetupField path={`${kind}.${item.key}.${field}`} label={label}>
        <InputNumber
            value={value}
            min={min}
            precision={0}
            placeholder={nullable ? 'No limit' : undefined}
            disabled={form.readOnly}
            onChange={(next) =>
                form.setValue(
                    `${kind}.${index}.${field}`,
                    next ?? (nullable ? null : value)
                )
            }
        />
    </SetupField>
);

const IconField = <T extends Doc31Bonus | Doc31Badge>({
    form,
    kind,
    item,
    index,
}: ItemFieldProps<T>) => (
    <SetupField path={`${kind}.${item.key}.iconUrl`} label="Icon">
        {/* ImageInput has no disabled state: show the icon alone instead. */}
        {form.readOnly ? (
            item.iconUrl ? (
                <img
                    className={style.icon_preview}
                    src={item.iconUrl}
                    alt={`${item.key} icon`}
                />
            ) : (
                <span className={style.muted}>No icon</span>
            )
        ) : (
            <ImageInput
                url={item.iconUrl}
                onChange={(url) =>
                    form.setValue(`${kind}.${index}.iconUrl`, url)
                }
                onDelete={() => form.setValue(`${kind}.${index}.iconUrl`, null)}
            />
        )}
    </SetupField>
);

interface ItemCardProps<T extends Doc31Bonus | Doc31Badge>
    extends ItemFieldProps<T> {
    isExpanded: boolean;
    onToggle: () => void;
    /** One line under the title: what the item gives and its limits. */
    summary: string;
    /** The item's own settings, shown under the copy. */
    children: React.ReactNode;
}

/** One bonus or badge: header always shown, settings when opened. */
const ItemCard = <T extends Doc31Bonus | Doc31Badge>({
    form,
    kind,
    item,
    index,
    isExpanded,
    onToggle,
    summary,
    children,
}: ItemCardProps<T>) => {
    const title = item.title.en.trim() || item.key;
    return (
        <div
            id={doc31FieldId(`${kind}.${item.key}`)}
            className={style.item_card}
        >
            <div
                className={`${style.item_header} ${
                    isExpanded ? style.item_header_open : ''
                }`}
            >
                <div className={style.item_heading}>
                    <span className={style.item_title}>
                        <span className="body_bold">{title}</span>
                        <span className={style.muted}>{item.key}</span>
                    </span>
                    <span className={style.item_summary}>{summary}</span>
                </div>
                <div className={style.item_actions}>
                    <span
                        id={doc31FieldId(`${kind}.${item.key}.enabled`)}
                        className={style.item_switch_row}
                    >
                        <span>On</span>
                        <Switch
                            aria-label={`${title} on`}
                            checked={item.enabled}
                            disabled={form.readOnly}
                            onChange={(checked) =>
                                form.setValue(
                                    `${kind}.${index}.enabled`,
                                    checked
                                )
                            }
                        />
                    </span>
                    <button
                        type="button"
                        className={style.collapse_toggle}
                        aria-label={`${
                            isExpanded ? 'Collapse' : 'Expand'
                        } ${title}`}
                        aria-expanded={isExpanded}
                        onClick={onToggle}
                    >
                        {isExpanded ? <UpOutlined /> : <DownOutlined />}
                    </button>
                </div>
            </div>
            {isExpanded && (
                <div className={style.item_body}>
                    <div className="columns_layout">
                        {LANGUAGES.map((language) => (
                            <div key={language} className="column">
                                <span className="h5_bold">
                                    {LANGUAGE_NAMES[language]}
                                </span>
                                <SetupField
                                    path={`${kind}.${item.key}.title.${language}`}
                                    label={`Title (${LANGUAGE_NAMES[language]})`}
                                >
                                    <Input
                                        value={item.title[language]}
                                        disabled={form.readOnly}
                                        onChange={(event) =>
                                            form.setValue(
                                                `${kind}.${index}.title.${language}`,
                                                event.target.value
                                            )
                                        }
                                    />
                                </SetupField>
                                <SetupField
                                    path={`${kind}.${item.key}.description.${language}`}
                                    label={`Description (${LANGUAGE_NAMES[language]})`}
                                >
                                    <Input.TextArea
                                        autoSize={{ minRows: 3, maxRows: 8 }}
                                        value={item.description[language]}
                                        disabled={form.readOnly}
                                        onChange={(event) =>
                                            form.setValue(
                                                `${kind}.${index}.description.${language}`,
                                                event.target.value
                                            )
                                        }
                                    />
                                </SetupField>
                            </div>
                        ))}
                    </div>
                    <div className={style.item_fields}>{children}</div>
                </div>
            )}
        </div>
    );
};

export const BonusesSection = ({
    form,
    expandedKeys,
    onToggleExpanded,
    saveAction,
}: CatalogProps) => (
    <Section
        id={doc31FieldId('bonuses')}
        title="Bonus actions"
        headerActions={saveAction}
    >
        <div className={style.item_list}>
            {form.values.bonuses.map((bonus, index) => {
                const fieldProps = {
                    form,
                    kind: 'bonuses' as const,
                    item: bonus,
                    index,
                };
                const summary = [
                    `${bonus.entries} ${
                        bonus.entries === 1 ? 'entry' : 'entries'
                    }`,
                    bonus.isOneTime && 'once per edition',
                    bonus.maxPerUser !== null &&
                        `max ${bonus.maxPerUser}/player`,
                    bonus.maxPerDay !== null && `max ${bonus.maxPerDay}/day`,
                ]
                    .filter(Boolean)
                    .join(' · ');
                return (
                    <ItemCard
                        key={bonus.key}
                        {...fieldProps}
                        isExpanded={expandedKeys.includes(bonus.key)}
                        onToggle={() => onToggleExpanded('bonuses', bonus.key)}
                        summary={summary}
                    >
                        <NumberField
                            {...fieldProps}
                            field="entries"
                            value={bonus.entries}
                            label="Entries"
                            min={0}
                        />
                        <SetupField
                            path={`bonuses.${bonus.key}.isOneTime`}
                            label="Once per edition"
                        >
                            <Switch
                                className={style.toggle}
                                checked={bonus.isOneTime}
                                disabled={form.readOnly}
                                onChange={(checked) =>
                                    form.setValue(
                                        `bonuses.${index}.isOneTime`,
                                        checked
                                    )
                                }
                            />
                        </SetupField>
                        <NumberField
                            {...fieldProps}
                            field="maxPerUser"
                            value={bonus.maxPerUser}
                            nullable
                            label="Max per player"
                            min={1}
                        />
                        <NumberField
                            {...fieldProps}
                            field="maxPerDay"
                            value={bonus.maxPerDay}
                            nullable
                            label="Max per day"
                            min={1}
                        />
                        <NumberField
                            {...fieldProps}
                            field="sortOrder"
                            value={bonus.sortOrder}
                            label="Order"
                            min={0}
                        />
                        <IconField {...fieldProps} />
                    </ItemCard>
                );
            })}
        </div>
    </Section>
);

export const BadgesSection = ({
    form,
    expandedKeys,
    onToggleExpanded,
    saveAction,
}: CatalogProps) => (
    <Section
        id={doc31FieldId('badges')}
        title="Badges"
        headerActions={saveAction}
    >
        <div className={style.item_list}>
            {form.values.badges.map((badge, index) => {
                const fieldProps = {
                    form,
                    kind: 'badges' as const,
                    item: badge,
                    index,
                };
                const goalLabel = THRESHOLD_LABELS[badge.ruleType];
                const summary = [
                    goalLabel &&
                        badge.threshold !== null &&
                        `${goalLabel}: ${badge.threshold}`,
                    `${badge.entries} ${
                        badge.entries === 1 ? 'entry' : 'entries'
                    }`,
                ]
                    .filter(Boolean)
                    .join(' · ');
                return (
                    <ItemCard
                        key={badge.key}
                        {...fieldProps}
                        isExpanded={expandedKeys.includes(badge.key)}
                        onToggle={() => onToggleExpanded('badges', badge.key)}
                        summary={summary}
                    >
                        {goalLabel ? (
                            <NumberField
                                {...fieldProps}
                                field="threshold"
                                value={badge.threshold}
                                label={`Goal (${goalLabel.toLowerCase()})`}
                                min={1}
                            />
                        ) : (
                            <LabeledField label="Rule">
                                <span>{badge.ruleType}</span>
                            </LabeledField>
                        )}
                        <NumberField
                            {...fieldProps}
                            field="entries"
                            value={badge.entries}
                            label="Entries"
                            min={0}
                        />
                        <NumberField
                            {...fieldProps}
                            field="sortOrder"
                            value={badge.sortOrder}
                            label="Order"
                            min={0}
                        />
                        <IconField {...fieldProps} />
                    </ItemCard>
                );
            })}
        </div>
    </Section>
);
